import React, { useEffect, useState } from "react";
import { PiPhoneCallFill } from "react-icons/pi";
import {
  SiReact,
  SiJavascript,
  SiTailwindcss,
  SiHtml5,
  SiCss3,
  SiNodedotjs,
  SiPython,
  SiPandas,
  SiPowerbi,
} from "react-icons/si";
import AppointmentModal from "../Common/AppointmentModal";

const typingWords = [
  "Offline Courses",
  "Online Courses",
  "Hybrid Courses",
  "Live Workshops",
];

const relatedLines = {
  "Offline Courses":
    "Classroom sessions with hands-on labs and mentor support.",
  "Online Courses":
    "Flexible schedule, recorded lessons and live doubt-clearing.",
  "Hybrid Courses": "Best of both worlds — classroom + online resources.",
  "Live Workshops": "Intensive hands-on workshops with real projects.",
};

const HeroSection = () => {
  const [open, setOpen] = useState(false);
  const [displayText, setDisplayText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  useEffect(() => {
    let timeoutId;
    const currentWord = typingWords[wordIndex % typingWords.length];

    if (!isDeleting && displayText.length < currentWord.length) {
      // typing forward
      timeoutId = setTimeout(() => {
        setDisplayText(currentWord.slice(0, displayText.length + 1));
      }, typingSpeed);
    } else if (!isDeleting && displayText.length === currentWord.length) {
      // pause at end, then start deleting
      timeoutId = setTimeout(() => setIsDeleting(true), 1200);
    } else if (isDeleting && displayText.length > 0) {
      // deleting
      timeoutId = setTimeout(() => {
        setDisplayText(currentWord.slice(0, displayText.length - 1));
      }, Math.max(40, typingSpeed / 2)); // delete a bit faster, but bounded
    } else if (isDeleting && displayText.length === 0) {
      // move to next word
      setIsDeleting(false);
      setWordIndex((prev) => (prev + 1) % typingWords.length);
      timeoutId = setTimeout(() => {}, 200);
    }

    return () => clearTimeout(timeoutId);
  }, [displayText, isDeleting, wordIndex, typingSpeed]);

  useEffect(() => {
    const currentWord = typingWords[wordIndex % typingWords.length];
    setTypingSpeed(currentWord.length > 12 ? 110 : 90);
  }, [wordIndex]);

  const handleAddOpen = () => setOpen(true);
  const handleModalClose = () => setOpen(false);

  const currentTypedWord = typingWords[wordIndex % typingWords.length];
  const currentRelated = relatedLines[currentTypedWord] || "";

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* caret & float styles */}
      <style>{`
        .typing-caret::after{
          content: "";
          display: inline-block;
          width: 2px;
          height: 1.05em;
          margin-left: 6px;
          background: linear-gradient(180deg,#cfe9ff,#6fb1ff);
          animation: blink 1s steps(2,start) infinite;
          vertical-align: middle;
        }
        @keyframes blink { 50% { opacity: 0; } }

        /* Floating animation for icon bubbles */
        @keyframes floatY {
          0% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-18px) rotate(8deg); }
          100% { transform: translateY(0) rotate(0deg); }
        }

        /* subtle horizontal drift for variety */
        @keyframes driftX {
          0% { transform: translateX(0); }
          50% { transform: translateX(6px); }
          100% { transform: translateX(0); }
        }

        /* default float */
        .float {
          animation: floatY 4.8s ease-in-out infinite;
        }

        /* make slow/fast usable alone (they now include animation shorthand) */
        .float-slow {
          animation: floatY 6.4s ease-in-out infinite;
        }
        .float-fast {
          animation: floatY 3.8s ease-in-out infinite;
        }

        /* delays still apply */
        .float-delay-1 { animation-delay: 0.2s; }
        .float-delay-2 { animation-delay: 0.6s; }
        .float-delay-3 { animation-delay: 1.0s; }
        .float-delay-4 { animation-delay: 1.4s; }

        /* tilt uses two animations (vertical float + horizontal drift) */
        .float-tilt { 
          animation-name: floatY, driftX; 
          animation-duration: 5s, 8s; 
          animation-timing-function: ease-in-out, ease-in-out; 
          animation-iteration-count: infinite, infinite; 
        }

        /* icon bubble look */
        .tech-bubble {
          width: 64px;
          height: 64px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          backdrop-filter: blur(6px);
          border: 1px solid rgba(255,255,255,0.06);
          box-shadow: 0 10px 30px rgba(6,69,133,0.12);
        }

        /* responsive scaling */
        @media (min-width: 1024px) {
          .tech-bubble { width: 80px; height: 80px; border-radius: 18px; }
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -right-32 -top-24 h-72 w-72 rounded-full bg-[#064585]/40 blur-3xl" />
        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#5989C8]/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* LEFT CONTENT */}
          <div className="flex flex-col justify-center text-center md:text-left space-y-5">
            <div
              className="inline-flex items-center justify-center md:justify-start mx-auto md:mx-0 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm text-xs sm:text-sm text-[#5989C8] font-medium"
              data-aos="fade-up"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400 mr-2" />
              Own Your Future Learning New Skills On WBC
            </div>

            <div data-aos="flip-up" className="">
              <h1 className="text-3xl lg:text-5xl font-semibold lg:leading-snug text-white">
                Engaging & Accessible
              </h1>
              <h1
                className="text-3xl lg:text-5xl font-semibold lg:leading-snug bg-gradient-to-r from-[#64a2ff] to-[#064585] bg-clip-text text-transparent typing-caret"
                aria-hidden="true"
              >
                {displayText}
              </h1>
              <p className="mt-3 lg:text-lg text-slate-200/90 min-h-[48px]">
                {currentRelated}
              </p>
            </div>

            <div className="w-full max-w-sm mx-auto md:mx-0">
              <div
                className="flex flex-col sm:flex-row sm:items-center gap-5 justify-center md:justify-start"
                data-aos="zoom-in-down"
              >
                <button
                  onClick={handleAddOpen}
                  className="inline-flex items-center justify-center rounded-full bg-[#064585] px-4 lg:px-8 py-3 text-sm lg:text-base font-semibold text-white shadow-lg shadow-[#064585]/40 hover:bg-[#033465] transition-all duration-200 hover:-translate-y-0.5"
                >
                  Talk to Us
                </button>

                <a
                  href="tel:9861934932"
                  className="flex flex-1 items-center justify-center sm:justify-start gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 backdrop-blur-md"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#064585]/10">
                    <PiPhoneCallFill className="text-[#5989C8] text-xl" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-slate-300">Contact Us</p>
                    <p className="text-base font-semibold text-white">
                      +91 9861934932
                    </p>
                  </div>
                </a>
              </div>

              {/* stats */}
              <div
                className="mt-4 grid grid-cols-3 gap-3 text-sm text-slate-200/90"
                data-aos="fade-up"
              >
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <p className="font-semibold text-white">10K+</p>
                  <p className="opacity-80">Learners</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <p className="font-semibold text-white">5+</p>
                  <p className="opacity-80">Courses</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <p className="font-semibold text-white">4.9★</p>
                  <p className="opacity-80">Ratings</p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div
            className="relative flex justify-center md:justify-end pointer-events-none"
            data-aos="zoom-in-left"
            aria-hidden="true"
          >
            <div className="relative w-full max-w-sm md:max-w-md lg:max-w-lg">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-[#064585]/30 via-[#5989C8]/15 to-transparent blur-3xl" />

              <div className="relative rounded-3xl bg-white/4 border border-white/6 backdrop-blur-md p-8 shadow-2xl flex items-center justify-center">
                <div className="relative w-full h-64 md:h-80 lg:h-96">
                  {/* React */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 float float-delay-1">
                    <div className="tech-bubble bg-white/20">
                      <SiReact className="text-[#61DAFB] text-4xl" />
                    </div>
                  </div>

                  {/* JavaScript */}
                  <div className="absolute -left-6 top-4 float-slow float-delay-2">
                    <div className="tech-bubble bg-white/20">
                      <SiJavascript className="text-[#F7E018] text-3xl" />
                    </div>
                  </div>

                  {/* Python */}
                  <div className="absolute right-6 top-6 float float-delay-3">
                    <div className="tech-bubble bg-white/20">
                      <SiPython className="text-[#306998] text-3xl" />
                    </div>
                  </div>

                  {/* Pandas */}
                  <div className="absolute left-24 bottom-4 float-slow float-delay-1">
                    <div className="tech-bubble bg-white/20">
                      <SiPandas className="text-[#150458] text-3xl" />
                    </div>
                  </div>

                  {/* PowerBI */}
                  <div className="absolute right-12 bottom-6 float-fast float-delay-4">
                    <div className="tech-bubble bg-white/20">
                      <SiPowerbi className="text-[#F2C811] text-3xl" />
                    </div>
                  </div>

                  {/* HTML */}
                  <div className="absolute left-1/4 -top-8 float-slow float-delay-3">
                    <div className="tech-bubble bg-white/20">
                      <SiHtml5 className="text-[#E54C21] text-2xl" />
                    </div>
                  </div>

                  {/* CSS */}
                  <div className="absolute right-1/2 top-24 float-fast float-delay-1">
                    <div className="tech-bubble bg-white/20">
                      <SiCss3 className="text-[#0A73B8] text-2xl" />
                    </div>
                  </div>

                  {/* Tailwind */}
                  <div className="absolute left-2 bottom-1/4 float float-delay-2">
                    <div className="tech-bubble bg-white/20">
                      <SiTailwindcss className="text-[#38BDF8] text-3xl" />
                    </div>
                  </div>

                  {/* NodeJS */}
                  <div className="absolute right-2 top-1/3 float-slow float-delay-4">
                    <div className="tech-bubble bg-white/20">
                      <SiNodedotjs className="text-[#3C873A] text-3xl" />
                    </div>
                  </div>

                  {/* Mini glowing spots */}
                  <div className="absolute -left-10 -bottom-8 w-20 h-20 rounded-full bg-[#5989C8]/10 blur-xl" />
                  <div className="absolute right-[-14px] top-[-12px] w-20 h-20 rounded-full bg-[#064585]/10 blur-xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Modal */}
      {open && <AppointmentModal onClose={handleModalClose} />}
    </section>
  );
};

export default HeroSection;
